import { AdobeAnalyticsUpdate } from "../../../helpers/analytics.service";

export interface ChatbotConfiguration {
    isAvailable: boolean;
    ctaIconSvg: string;
    chatIconSvg: string;
    ctaBannerIconSvg: string;
    dialogFlowAgent: string;
    defaultLanguage: string;
    enableSpeechToText: boolean
    tunnelPageUrl: string;
    chatbotName: string;
    callToActions: ChatbotCallToAction[];
    notificationMessages: {[key: string]: ChatbotConfigurationMessages};
    availableLanguages: string[];
    translationsBaseUrl: string;
    maxFileSize: number;
    allowedFileExtensions: string[];
    disableWritingTimeChatbot: boolean;
    enableFileUploadLiveAgent: boolean;
};

export interface ChatbotButtonConfiguration {
    configurationName: string;
    isAvailable: boolean;
    chatIconSvg: string;
    dialogFlowAgent: string;
    tunnelPageUrl: string;
    translationsBaseUrl: string;
    chatbotName: string;
    availableLanguages: string[];
    defaultLanguage:string;
    enableSpeechToText: boolean
    ctaOfflineLinkText: string;
    ctaOfflineLinkUri: string;
    buttonText: string;
    buttonType: ButtonType;
    startIntent: string;
    welcomeIntent: string;
    notificationMessages: {[key: string]: ChatbotConfigurationMessages};
    maxFileSize: number;
    allowedFileExtensions: string[];
    disableWritingTimeChatbot: boolean;
    enableFileUploadLiveAgent: boolean;
}

export interface ChatbotFloatingButtonConfiguration {
    configurationName: string;
    isAvailable: boolean;
    chatIconSvg: string;
    floatingButtonIconSvg: string;
    dialogFlowAgent: string;
    tunnelPageUrl: string;
    translationsBaseUrl: string;
    chatbotName: string;
    availableLanguages: string[];
    defaultLanguage:string;
    enableSpeechToText: boolean
    ctaOfflineLinkText: string;
    ctaOfflineLinkUri: string;
    buttonText: string;
    buttonType: ButtonType;
    startIntent: string;
    welcomeIntent: string;
    notificationMessages: {[key: string]: ChatbotConfigurationMessages};
    maxFileSize: number;
    allowedFileExtensions: string[];
    disableWritingTimeChatbot: boolean;
    type: FloatingButtonType;
    enableFileUploadLiveAgent: boolean;
}

export enum FloatingButtonType {
    Bubble = 'Bubble',
    TextBar = 'TextBar'
}

export enum ButtonType
{
    Primary = 'Primary',
    Secondary = 'Secondary',
    Tertiary = 'Tertiary'
}

export enum ChatbotTriggerType {
    defaultCallToAction = 'DefaultCallToAction',
    autoOpen = 'AutoOpen',
    autoOpenForApp = 'AutoOpenForApp',
    banner = 'Banner',
    aurora = 'Aurora',
    contactCard= 'ContactCard'
};

export enum ChatbotTriggerColor {
    gray = 'Gray',
    orange = 'Orange',
    tertGray = 'TertGray'
};

export enum ChatbotCallToActionPriority {
    primary = 'Primary',
    secondary = 'Secondary'
};

export interface ChatbotConfigurationMessages {
    commonError : string;
    liveChatError : string;
    inactivityError : string;
    liveChatConnected : string;
    liveChatEnded : string;
    chatReset : string;
    fileUploadError: string;
    transferMessages: string[]
};

export interface ChatbotCallToAction {
    priority: ChatbotCallToActionPriority;
    startIntents: ChatbotStartIntent[];
    ctaOfflineLinkText: string;
    ctaOfflineLinkUri: string;
    ctaOfflineSubtext: string;
    ctaOfflineTitle: string;
    ctaOnlineSubtext: string;
    ctaOnlineTitle: string;
    triggerType: ChatbotTriggerType;
    triggerColor: ChatbotTriggerColor;
    autoOpenScroll: ChatbotAutoOpenScroll;
    amountOfContactCardsOnRow: number;
    contactCards: ChatbotContactCard[];
};

export interface ChatbotStartIntent {
    startIntent: string;
    buttonText: string;
    welcomeIntent: string;
    index: number;
};

export interface ChatbotResponse {
    text: string[];
    suggestions: ChatbotResponseButton[];
    quickReplies: ChatbotResponseButton[];
    multiSelect: ChatbotMultiSelect;
    liveChat: ChatbotResponseLiveChat;
    adobeAnalyticsUpdate: AdobeAnalyticsUpdate;
    fakeInput: ChatbotResponseFakeInput;
    optionsButtons: ChatbotResponseOptionButtons;
    needsLogin: boolean;
    needsBusinessLogin: boolean;
    loginFollowUpEvent: string;
    notLoginFollowUpEvent: string;
    informationCard: ChatbotInformationCard;
    casesCard: ChatbotCasesCard;
    caseOverview: ChatbotCaseOverview;
    doNotTranslateInput: ChatbotDoNotTranslateInput;
    uploadFileButton: ChatbotUploadFileButton;
    showAllQuickReplies: boolean;
    datePicker: ChatbotDatePicker;
    faqs: ChatbotFaq[];
    trackedShipmentsCard: ChatbotTrackedShipmentsCard;
    disableSpeechToText: ChatbotDisableSpeechToText;
    overwriteAccessToken: ChatbotOverwriteAccessToken
};
export interface ChatbotFaq {
    title: string;
    id: string;
    introQuickQuestion: string;
    expanded: boolean;
    url: string;
}
export interface ChatbotMultiSelect {
    title: string;
    singleSelectButtons: string[];
    multiSelectButtons: ChatbotMultiSelectButton[];
};

export interface ChatbotMultiSelectButton {
    text: string;
    data: any;
};

export interface ChatbotResponseButton {
    text: string;
    responseMessage: string;
    imageUrl: string;
};

export interface ChatbotResponseLiveChat {
    consumerName: string;

    consumerId: string;

    language: string;

    agentSkill: string;

    caseNumber: string;
};

export interface ChatbotResponseFakeInput{
    text: string;
};

export interface ChatbotResponseOptionButtons {
    saveOrPrint: boolean;
    reset: boolean;
    close: boolean;
    promoteApp: boolean;
};

export interface ChatbotInformationCard {
    title: string;
    items:  ChatbotInformationCardItem[];
};

export interface ChatbotInformationCardItem {
    key: string;
    value: string;
};

export interface ChatbotCasesCard {
    title: string;
    caseItems:  ChatbotCasesCardItem[];
};

export interface ChatbotCasesCardItem {
    caseNumber: string;
    status: string;
    title: string;
    description?: string;
};

export interface ChatbotDoNotTranslateInput {
    value: boolean;
}

export interface ChatbotUploadFileButton {
    text: string;
    followupEvent: string;
    isOptional: boolean
    noFileUploadText?: string;
    fileCategory?: string;
    noFileUploadFollowUpEvent?: string;
};

export interface ChatbotCaseOverview {
    caseNumber: string;
    senderName: string
    receiverName: string;
    barcode: string;
    status: string;
    createdDate: string;
    closedDate?: string;
    subject: string;
    description: string;
    followUpEvent: string;
}

export enum CaseStatusEnum {
    New = 0,
    InProgress = 1,
    Closed = 2
}

export interface ChatbotDatePicker {
    showDatePicker: boolean;
    lockWeekendDays?: boolean;
    lockSundaysAndMondays?: boolean;
    lockDays: string[];
    minDate?: Date;
    maxDate?: Date;
    lockPastDays?: boolean;
    lockPastDaysInclToday?: boolean;
    lockFutureDays?: boolean;
    lockFutureDaysInclToday?: boolean;
}

export interface ChatbotAutoOpenScroll {
    isActive: boolean;
    scrollPercentage: number;
    startIntent: string;
    welcomeIntent: string;
}

export interface ChatbotContactCard {
    svg: string;
    title: string;
    closedTitle: string;
    closedSvg: string;
    subtext: string;
    closedSubtext: string;
    buttonText: string;
    startIntent: string;
    welcomeIntent: string;
    alwaysOpen: boolean;
    openTill?: string;
    openAt?: string;
    waitingTime?: number;
    buttonType: ButtonType;
    isOpen: boolean
    index: number;
}

export interface ChatbotTrackedShipmentsCard {
    title: string;
    trackedShipments:  ChatbotTrackedShipmentCardItem[];
};

export interface ChatbotTrackedShipmentCardItem {
    title: string;
    barcode: string;
    isDelivered: string;
    deliveryWindowFrom?: string;
    deliveryWindowTo?: string;
    deliveredTimeStamp?: string;
    detailsApiUrl: string;
    updatedDate: string;
};

export interface ChatbotDisableSpeechToText {
    value: boolean;
}

export interface ChatbotOverwriteAccessToken {
    followupEvent: string;
};

export interface ChatbotNotification {
    title: string;
    subtext: string;
    type: NotificationType;
}

export enum NotificationType {
    success = 0,
    warning = 1,
    error = 2,
    info = 3
}